import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { isUkPostcode } from '@sainsburys-tech/bolt-validators'
import { HelpCircleFilled, CheckCircleFilled } from '@sainsburys-tech/icons'
import collectionMessaging from '../../../config/fulfilment-collection-messaging.json'
import featureTypeMessaging from '../../../config/feature-type-messaging.json'
import FulfilmentSkeleton from '../FulfilmentSkeleton'

import { useFeatureClassName } from '@/javascript/hooks/useFeatureClassName'
import * as Styled from './CheckStoreBox.styles'

const CheckStoreBox = ({ props, searchStock, findButtonText, UpdateInitialSearchTerm, initialSearchTerm }) => {
  const { className } = useFeatureClassName()
  const [emptyRequest, setEmptyRequest] = useState(false)
  const [invalidPostcode, setInvalidPostcode] = useState(false)
  const fulfilmentHeading = 'Check stock'
  const notOutOfStockMessage = 'Check stock to see your delivery and collection options:'
  const checkStoreInputPlaceholder = 'Postcode or town'
  const dataTest = 'fulfilment-search-stock'
  const {
    storeLocator: {
      deliveryError,
      collectionError,
      stockCheckAvailable,
      collectionLoading,
      deliveryLoading,
      regionalIsAvailable,
      regionalMessage
    },
    attributes
  } = props

  const {
    globallyOutOfStock,
    endOfLineOutOfStock,
    preorderUnavailable,
    inStoreJewelleryItem,
    digitalDownload,
    twoManDeliveryUnavailable
  } = attributes

  const hasFeatureType =
    globallyOutOfStock ||
    endOfLineOutOfStock ||
    preorderUnavailable ||
    inStoreJewelleryItem ||
    digitalDownload ||
    twoManDeliveryUnavailable

  const collectable = attributes.collectable && !hasFeatureType
  const deliverable = attributes.deliverable && !hasFeatureType
  const showRegionalAvailability = typeof regionalIsAvailable !== 'undefined' && typeof regionalMessage !== 'undefined'

  const localSearchStock = event => {
    event.preventDefault()
    if (!initialSearchTerm) {
      setEmptyRequest(true)
    } else if (deliverable && !collectable && !isUkPostcode(initialSearchTerm.trim())) {
      setInvalidPostcode(true)
    } else {
      setEmptyRequest(false)
      searchStock(event)
    }
  }

  const evaluateStockMessage = () => {
    if (showRegionalAvailability) {
      return { heading: regionalMessage, message: notOutOfStockMessage }
    }
    if (endOfLineOutOfStock) {
      return featureTypeMessaging.END_OF_LINE_OOS
    }
    if (preorderUnavailable) {
      return featureTypeMessaging.PRE_ORDER_UNAVAILABLE
    }
    if (inStoreJewelleryItem) {
      return featureTypeMessaging.JEWELLERY_INSTORE_ONLY
    }
    if (digitalDownload) {
      return featureTypeMessaging.DIGITAL_DOWNLOAD
    }
    if (twoManDeliveryUnavailable) {
      return featureTypeMessaging.TWO_MAN_DELIVERY
    }
    if ((collectable || deliverable) && !globallyOutOfStock) {
      return { heading: fulfilmentHeading, message: notOutOfStockMessage }
    }
    const key = globallyOutOfStock ? 'GLOBALLY_OUT_OF_STOCK' : 'NOT_AVAILABLE_ONLINE'
    return featureTypeMessaging[key]
  }

  const hideFulfilmentHeader =
    (!deliverable && !collectable) ||
    ((collectable ? !!props.storeLocator.collectionMessage : true) &&
      (deliverable && !collectable ? !!props.storeLocator.deliveryMessage : true))

  const notAvailableOnline = !deliverable && !collectable
  const placeholder = deliverable && !collectable ? 'Enter postcode' : checkStoreInputPlaceholder
  const loading = (collectionLoading || deliveryLoading) && !hideFulfilmentHeader && !showRegionalAvailability
  const show = (!hasFeatureType && !hideFulfilmentHeader) || showRegionalAvailability
  const stockStatusMessage = evaluateStockMessage()
  const getErrorMessage = () => {
    if (emptyRequest) return 'Remember to enter a town or postcode'
    if (invalidPostcode) return `Oops, we didn't recognise those details. Please try again.`
    if (deliveryError && !emptyRequest) return deliveryError
    if (collectionError) return collectionError
    if (!stockCheckAvailable) return collectionMessaging.STOCK_CHECK_FAIL
    return ''
  }

  const regionalAvailabilityIcons = {
    in_stock: <CheckCircleFilled data-test='in-stock-icon' className='ds-text-green-600' height={20} width={20} />,
    out_of_stock: <HelpCircleFilled data-test='out-of-stock-icon' className='ds-text-blue-600' height={20} width={20} />
  }

  const regionalAvailabilityIcon = regionalIsAvailable
    ? regionalAvailabilityIcons.in_stock
    : regionalAvailabilityIcons.out_of_stock

  return (
    <Styled.FulfilmentBox data-test='check-store-box' className={className}>
      {loading && <FulfilmentSkeleton />}
      {((!loading && !hideFulfilmentHeader) || notAvailableOnline) && (
        <Styled.FulfilmentHeading id='fulfilment-search-stock-search-input-search-label' data-test='fulfilment-heading'>
          {showRegionalAvailability && regionalAvailabilityIcon}
          <strong>{stockStatusMessage.heading}</strong>
        </Styled.FulfilmentHeading>
      )}
      {((!loading && !hideFulfilmentHeader) || notAvailableOnline) && <p>{stockStatusMessage.message}</p>}
      {!loading && show && (
        <form onSubmit={localSearchStock} data-test={`${dataTest}-form`}>
          <Styled.InputContainer
            type='text'
            aria-label='Search Stock'
            value={initialSearchTerm}
            onChange={UpdateInitialSearchTerm}
            placeholder={placeholder}
            name='search'
            autoComplete='off'
            error={getErrorMessage()}
            data-test={`${dataTest}-search-input`}
            buttonOptions={{
              text: findButtonText,
              type: 'submit',
              show: true,
              kind: 'secondary',
              'data-test': `${dataTest}-search-button`
            }}
          />
        </form>
      )}
    </Styled.FulfilmentBox>
  )
}

CheckStoreBox.propTypes = {
  props: PropTypes.shape({
    attributes: PropTypes.shape({
      globallyOutOfStock: PropTypes.bool,
      endOfLineOutOfStock: PropTypes.bool,
      preorderUnavailable: PropTypes.bool,
      inStoreJewelleryItem: PropTypes.bool,
      digitalDownload: PropTypes.bool,
      twoManDeliveryUnavailable: PropTypes.bool,
      collectable: PropTypes.bool,
      deliverable: PropTypes.bool
    }).isRequired,
    storeLocator: PropTypes.shape({
      deliveryError: PropTypes.string,
      collectionError: PropTypes.string,
      stockCheckAvailable: PropTypes.bool,
      collectionLoading: PropTypes.bool,
      deliveryLoading: PropTypes.bool,
      regionalIsAvailable: PropTypes.bool,
      regionalMessage: PropTypes.string,
      collectionMessage: PropTypes.string,
      deliveryMessage: PropTypes.string
    }).isRequired
  }).isRequired,
  searchStock: PropTypes.func.isRequired,
  findButtonText: PropTypes.string.isRequired,
  UpdateInitialSearchTerm: PropTypes.func.isRequired,
  initialSearchTerm: PropTypes.string.isRequired
}

export default CheckStoreBox
